import murmur from 'murmurhash'
import { DataType, Operator } from '../proto/generated/screener'
import { IndicatorInput_InputType } from '../proto/generated/indicator'
import { SettingType } from '../components/chartV2/drawingTools/drawing.enums'

export type Change = {
    change: number
    changePercentage: number
}

export function getColorForSymbol(symbol: string, defaultBackgroundColor: string) {
    if (!symbol) return defaultBackgroundColor
    const hashCode = murmur.v3(symbol)
    return `#${(hashCode >>> 0).toString(16).slice(-6)}`
}

export const getTimeAgoText = (inputDate: Date) => {
    const currentDate = new Date()
    const timeDifference = currentDate.getTime() - inputDate.getTime()

    // Calculate time units
    const minutes = Math.floor(timeDifference / (1000 * 60))
    const hours = Math.floor(timeDifference / (1000 * 60 * 60))
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
    const months = Math.floor(timeDifference / (1000 * 60 * 60 * 24 * 30.44)) // Rough estimate for a month
    const years = Math.floor(timeDifference / (1000 * 60 * 60 * 24 * 365.25)) // Rough estimate for a year

    // Generate text based on the time difference
    if (years > 0) {
        return years === 1 ? '1 year ago' : `${years} years ago`
    } else if (months > 0) {
        return months === 1 ? '1 month ago' : `${months} months ago`
    } else if (days > 0) {
        return days === 1 ? '1 day ago' : `${days} days ago`
    } else if (hours > 0) {
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    } else if (minutes > 0) {
        return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
    } else {
        return 'now'
    }
}

export const shorthandOperator = (operator: Operator) => {
    switch (operator) {
        case Operator.EQUAL_TO:
            return 'equals'
        case Operator.GREATER_THAN:
            return '>'
        case Operator.IN:
            return 'in'
        case Operator.LESS_THAN_EQUAL_TO:
            return '<='
        case Operator.NOT_EQUAL_TO:
            return 'not equals'
        case Operator.MATCH:
            return 'matches'
        case Operator.NOT_IN:
            return 'not in'
        case Operator.GREATER_THAN_EQUAL_TO:
            return '>='
        case Operator.BETWEEN:
            return 'between'
        case Operator.LESS_THAN:
            return '<'
        case Operator.OUTSIDE:
            return 'outside'
    }
}

export const isNumberType = (dataType: DataType) => {
    return [DataType.BIGDECIMAL, DataType.DOUBLE, DataType.INTEGER, DataType.LONG].includes(dataType)
}

export function convertUpperCaseToTitleCase(input: string): string {
    const words = input.split('_')
    const titleCaseString = words
        .map((word, index) => {
            if (index === 0) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            }
            return word.toLowerCase()
        })
        .join(' ')

    return titleCaseString
}

export function calculateChange(curr: number, prev: number | undefined): Change {
    const change = curr - (prev ?? curr)
    const changePercentage = prev === 0 || !prev ? 0 : (change / prev) * 100
    return {
        change: change,
        changePercentage: changePercentage
    }
}

export function calculateChangeIndex(curr: string, prev: string): number {
    let len = Math.min(curr.length, prev.length)
    for (let i = 0; i < len; i++) {
        if (curr.charAt(i) === '.' || prev.charAt(i) === '.') {
            continue
        }
        if (curr.charAt(i) !== prev.charAt(i)) {
            return i
        }
    }
    if (curr.length > prev.length) {
        return len
    }
    return -1
}

export function dateFromDDMMYYYY(dateString: string): Date | null {
    const day = Number(dateString.substring(0, 2))
    const month = Number(dateString.substring(2, 4))
    const year = Number(dateString.substring(4, 8))

    // Check if the parsed components are valid
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
        console.error('Invalid date components')
        return null
    }

    return new Date(year, month, day)
}

export function getStartOfWeek(date: any, weekStartsOn = 1) {
    const d = new Date(date)
    const day = d.getDay()
    const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn
    d.setDate(d.getDate() - diff)
    d.setHours(0, 0, 0, 0)
    return d
}

export function getEndOfWeek(date: any, weekStartsOn = 1) {
    const start = getStartOfWeek(date, weekStartsOn)
    const end = new Date(start)
    end.setDate(end.getDate() + 6)
    end.setHours(23, 59, 59, 999)
    return end
}

export function getLastDayOfMonth(date: Date): Date {
    const nextMonth = new Date(date)
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    nextMonth.setDate(1)
    const lastDayOfMonth = new Date(nextMonth)
    lastDayOfMonth.setDate(lastDayOfMonth.getDate() - 1)
    return lastDayOfMonth
}

export function formatDateToISODate(date: Date, seperator: string): string {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0') // Months are zero-indexed
    const day = date.getDate().toString().padStart(2, '0')

    return `${year}${seperator}${month}${seperator}${day}`
}

export const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export const shortMonthNames: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const weekNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday',
    'Thursday', 'Friday', 'Saturday'
]

export const shortWeekNames = [
    'Sun', 'Mon', 'Tue', 'Wed',
    'Thu', 'Fri', 'Sat'
]

export function hexToRgb(hexCode: string): number[] {
    const hex = hexCode.replace(/^#/, '')
    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return [r, g, b]
}

export function hasPrefix(array: string[], string2: string): boolean {
    for (const str of array) {
        if (string2.startsWith(str)) {
            return true
        }
    }
    return false
}

export const openNewWindow = (url: string, target: string, widthRatio: number = 0.6, heightRatio: number = 0.6): Window | null => {
    const width = window.innerWidth * widthRatio
    const height = window.innerHeight * heightRatio
    const left = window.innerWidth / 2 - width / 2
    const top = window.innerHeight / 2 - height / 2
    const features = `popup,width=${width},height=${height},left=${left},top=${top}`
    return window.open(url, target, features)
}

export function replaceColorHashcodes(arr: string[], str: string) {
    const colorRegex = /#([a-fA-F0-9]{8}|[a-fA-F0-9]{6}|[a-fA-F0-9]{3})/g
    let currentIndex = 0

    return str.replace(colorRegex, (match, group) => {
        const replacement = currentIndex < arr.length ? arr[currentIndex] : match
        currentIndex++
        return replacement
    })
}

export function getTimeFromTimestamp(timestamp: number) {
    if (timestamp === 0) {
        return ['-', '-']
    }
    const date = new Date(timestamp)
    let hours = date.getHours()
    const ampm = hours >= 12 ? 'pm' : 'am'
    hours = hours % 12
    hours = hours ? hours : 12
    let minutes = date.getMinutes().toString()
    if (Number(minutes) < 10) {
        minutes = '0' + minutes
    }
    return [`${hours < 10 ? '0' + hours : hours}:${minutes}`, ampm]
}

export function mapIndicatorInputTypeToDrawingSettingType(type: IndicatorInput_InputType): SettingType {
    switch (type) {
        case IndicatorInput_InputType.BOOLEAN:
            return SettingType.BOOLEAN
        case IndicatorInput_InputType.FLOAT:
        case IndicatorInput_InputType.INTEGER:
        case IndicatorInput_InputType.LONG:
            return SettingType.NUMBER
        case IndicatorInput_InputType.STRING:
            return SettingType.TEXT
        default:
            return SettingType.TEXT
    }

}

export const formatGotoDate = (timestampInSeconds: number) => {
    const date = new Date(timestampInSeconds * 1000)
    return `${shortWeekNames[date.getDay()]} ${date.getDate().toString().padStart(2, '0')} ${shortMonthNames[date.getMonth()]} '${date.getFullYear().toString().slice(-2)}, ${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}:${date.getUTCSeconds().toString().padStart(2, '0')}`
}

export const binarySearch = (arr: number[], target: number): number => {
    let low = 0
    let high = arr.length - 1

    while (low <= high) {
        const mid = Math.floor((low + high) / 2)

        if (arr[mid] === target) {
            return mid
        } else if (arr[mid] < target) {
            low = mid + 1
        } else {
            high = mid - 1
        }
    }

    return -1
}

export const binarySearchFirstDuplicate = (arr: number[], target: number): number => {
    let low = 0
    let high = arr.length - 1

    while (low <= high) {
        const mid = Math.floor((low + high) / 2)

        if (arr[mid] === target) {
            // Check if this is the first occurrence (left side has all smaller values)
            if (mid === 0 || arr[mid - 1] !== target) {
                return mid
            } else {
                // Move left to find the first occurrence (duplicate on the right)
                high = mid - 1
            }
        } else if (arr[mid] < target) {
            low = mid + 1
        } else {
            high = mid - 1
        }
    }

    return -1
}

export const getStartIndex = (comparables: any[], key: string, comparee: number | null): number => {
    if (!comparee) {
        return NaN
    }
    let l = 0,
        r = comparables.length - 1
    while (l <= r) {
        const mid = Math.floor((l + r) / 2)
        const p = comparables[mid][key]
        if (p > comparee) {
            r = mid - 1
        } else {
            l = mid + 1
        }
    }
    return r + 1 === comparables.length ? NaN : r + 1
}

export const getEndIndex = (comparables: any[], key: string, comparee: number | null): number => {
    if (!comparee) {
        return NaN
    }
    let l = 0,
        r = comparables.length - 1
    while (l <= r) {
        const mid = Math.floor((l + r) / 2)
        const p = comparables[mid][key]
        if (p >= comparee) {
            r = mid - 1
        } else {
            l = mid + 1
        }
    }
    return l - 1 < 0 ? -1 : l - 1
}

export const checkSearchTokensPresence = (tokens: string[], target: string): boolean => {
    let ans: boolean = true
    tokens.forEach((token) => {
        ans = ans && target.toLowerCase().includes(token.toLowerCase())
    })
    return ans
}

export const getOpacityFromHexCode = (hexCode: string): number => {
    if (RegExp(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/).test(hexCode) && hexCode.length === 9) {
        return parseInt(hexCode.slice(-2), 16) / 255
    }
    return NaN
}

export function findClosest(numbers: number[], target: number): number | undefined {
    if (numbers.length === 0) {
        return undefined
    }
    // Sort the array
    numbers.sort((a, b) => a - b)
    // If target is less than the smallest number, return the smallest
    if (target <= numbers[0]) {
        return numbers[0]
    }
    // If target is greater than the largest number, return the largest
    if (target >= numbers[numbers.length - 1]) {
        return numbers[numbers.length - 1]
    }
    let left = 0
    let right = numbers.length - 1
    while (left <= right) {
        const mid = Math.floor((left + right) / 2)
        if (numbers[mid] === target) {
            return numbers[mid]
        }
        if (target < numbers[mid]) {
            if (mid > 0 && target > numbers[mid - 1]) {
                // Return the closest of mid and mid-1
                return Math.abs(numbers[mid] - target) < Math.abs(numbers[mid - 1] - target)
                    ? numbers[mid]
                    : numbers[mid - 1]
            }
            // Target is in the left half
            right = mid - 1
        } else {
            if (mid < numbers.length - 1 && target < numbers[mid + 1]) {
                // Return the closest of mid and mid+1
                return Math.abs(numbers[mid] - target) < Math.abs(numbers[mid + 1] - target)
                    ? numbers[mid]
                    : numbers[mid + 1]
            }
            // Target is in the right half
            left = mid + 1
        }
    }
    // This line should never be reached if the input is valid
    return 0
    throw new Error('No closest number found')
}

export const toLocaleStringIn = (str: number, options?: Intl.NumberFormatOptions) => {
    return str.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options
    })
}

export const isEmbeddedModeSet  = () => window.localStorage.getItem('isEmbedded') === 'true';

export const sortKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(sortKeys); 
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj)
            .sort() 
            .reduce((acc, key) => {
                acc[key] = sortKeys(obj[key]); 
                return acc;
            }, {} as Record<string, any>);
    }
    return obj; 
};

export function isMacOS(): boolean {
  if (typeof navigator === 'undefined') return false

  const uaData = (navigator as any)?.userAgentData
  if (uaData?.platform) {
    return uaData.platform === 'macOS'
  }

  const ua = navigator?.userAgent?.toLowerCase()

  return (
    ua?.includes('macintosh') ||
    ua?.includes('mac os x')
  ) && !ua?.includes('iphone') && !ua?.includes('ipad')
}
